import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import { Button, Tooltip } from '@mui/material';
import { useUnit } from 'effector-react';
import { MRT_ColumnDef } from 'material-react-table';
import { useSnackbar } from 'notistack';

import { CategoryDialog } from './CategoryDialog';
import { TableGridLocal } from '../../../../components/TableGridLocal';
import { ConfirmationDialog } from '../../../../components/UIKit/ConfirmationDialog';
import { Faq$, faqApi } from '../../../../effector/faq';
import { Category } from '../../../../types/faq';
import { getMessageFromFailedRequest } from '../../../../utils';

export const Categories: React.FC = () => {
  const { categories } = useUnit(Faq$);
  const { enqueueSnackbar } = useSnackbar();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<Category | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const loadCategories = useCallback(() => {
    faqApi.getCategories('');
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleAddCategory = useCallback(() => {
    setEditingCategory(null);
    setDialogOpen(true);
  }, []);

  const handleEditCategory = useCallback((category: Category) => {
    setEditingCategory(category);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingCategory(null);
    setDeleteCandidate(null);
    setDeleteError('');
  }, []);

  const handleUpdate = useCallback(() => {
    handleCloseDialog();
    loadCategories();
  }, [handleCloseDialog, loadCategories]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteCandidate) return;

    setIsDeleting(true);
    setDeleteError('');

    try {
      await faqApi.removeCategory(deleteCandidate.id);
      enqueueSnackbar('Категория удалена');
      handleUpdate();
    } catch (e) {
      const message = getMessageFromFailedRequest(e as Error);
      setDeleteError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteCandidate, enqueueSnackbar, handleUpdate]);

  const columns = useMemo<MRT_ColumnDef<Category>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Идентификатор',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'name',
        header: 'Название',
        filterVariant: 'text',
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'friendlyUrl',
        header: 'Url',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        id: 'edit',
        header: '',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <Tooltip title="Редактировать">
            <EditNoteRoundedIcon
              sx={{ cursor: 'pointer', marginTop: 0.5 }}
              onClick={() => handleEditCategory(original)}
            />
          </Tooltip>
        ),
      },
      {
        id: 'remove',
        header: '',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <Tooltip title="Удалить">
            <DeleteOutlineRoundedIcon
              sx={{ cursor: 'pointer', marginTop: 0.5, color: 'error.main' }}
              onClick={() => setDeleteCandidate(original)}
            />
          </Tooltip>
        ),
      },
    ],
    [handleEditCategory],
  );

  return (
    <>
      <Button variant="contained" color="primary" startIcon={<AddRoundedIcon />} onClick={handleAddCategory}>
        Добавить
      </Button>

      <div style={{ marginTop: '30px' }}>
        <TableGridLocal
          name="administration-faq-categories"
          columns={columns}
          data={categories}
          totalRowCount={categories.length}
          onFetchData={loadCategories}
        />
      </div>

      {dialogOpen && <CategoryDialog category={editingCategory} onClose={handleCloseDialog} onUpdate={handleUpdate} />}

      {deleteCandidate && (
        <ConfirmationDialog
          title="Удаление категории"
          description="Вы уверены, что хотите удалить категорию?"
          error={deleteError}
          isLoading={isDeleting}
          onConfirm={handleDeleteConfirm}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
};
